import { Timestamp } from '@firebase/firestore-types';
import { Languages, CodeArticleState, CodeArticleVersion } from '@livv/models';
import { fromUnixTime } from 'date-fns';
import { Translate } from 'next-translate';
import { STATUS_DATE_MANAGEMENT } from './const';
import { Variant } from '@components/Timeline/types';
import { getDate } from '@utils/date';

/** Legifrance often uses 2222 or 2999 as undefined future date, setting 100 year threshold here */
export const THRESHOLD_SECONDS_UPCOMING = 60 * 60 * 24 * 365 * 100;

const getVariant = (
    versionsSelected: CodeArticleVersion[],
    currentVersion: string,
): Variant | undefined => {
    const [oldValue, newValue] = [...versionsSelected].sort(
        // copying array to sort without mutating
        ({ dateDebut: startDate1 }, { dateDebut: startDate2 }) =>
            startDate1.seconds - startDate2.seconds,
    );
    if (versionsSelected.length === 2 && oldValue?.legifranceId === currentVersion)
        return 'compareBefore';
    if (newValue?.legifranceId === currentVersion) return 'compareAfter';

    return undefined;
};

type State = keyof typeof STATUS_DATE_MANAGEMENT;

const getVersionLabel = (
    startDateTimestamp: Timestamp,
    endDateTimestamp: Timestamp,
    t: Translate,
    state: CodeArticleState,
    lang: Languages,
): string | null => {
    const todayTimestamp = Date.now() / 1000;
    const startDate = getDate(fromUnixTime(startDateTimestamp.seconds), lang, true);
    let { isDateFinValid } = STATUS_DATE_MANAGEMENT[state as State];
    const { isDateDebutValid } = STATUS_DATE_MANAGEMENT[state as State];

    if (endDateTimestamp.seconds > todayTimestamp + THRESHOLD_SECONDS_UPCOMING) {
        // sometimes ABROGE_DIFF end year is set to 2222 or 2999
        isDateFinValid = false;
    }

    if (startDateTimestamp.seconds > todayTimestamp + THRESHOLD_SECONDS_UPCOMING) {
        // sometimes VIGEUR and VIGEUR_DIFF dates are in Future
        return t('date.upcoming');
    }

    if (isDateDebutValid && !isDateFinValid) {
        return startDateTimestamp.seconds < todayTimestamp
            ? t('date.since', { date: startDate }) // e.g VIGEUR
            : t('date.from', { date: startDate }); // e.g VIGEUR_DIFF
    }

    if (isDateDebutValid && isDateFinValid) {
        if (startDateTimestamp.isEqual(endDateTimestamp)) {
            return t('date.on', { date: startDate });
        }
        const endDate = getDate(fromUnixTime(endDateTimestamp.seconds), lang, true);

        return `${startDate} → ${endDate}`;
    }

    return null;
};

export { getVariant, getVersionLabel };
